import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  DashboardOutlined,
  BarChartOutlined,
  TeamOutlined,
  FileTextOutlined,
  BookOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  LeftOutlined,
} from '@ant-design/icons';

const Sidebar = ({ user, collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const location = useLocation();

  const mainNavItems = [
    {
      path: '/saatm-dashboard',
      label: 'SAATM Dashboard',
      icon: <BarChartOutlined />,
    },
    {
      path: '/yd-monitoring',
      label: 'YD Monitoring',
      icon: <DashboardOutlined />,
      roles: ['admin', 'state', 'afcac'],
    },
    {
      path: '/administration',
      label: 'Admin Dashboard',
      icon: <TeamOutlined />,
      roles: ['admin'],
      orgCodes: ['admin'],
    },
  ];

  const libraryNavItems = [
    {
      path: '/e-library',
      label: 'YD Regulations',
      icon: <BookOutlined />,
    },
    {
      path: '/definitions',
      label: 'Definitions',
      icon: <FileTextOutlined />,
    },
  ];

  const documentNavItems = [
    {
      path: '/documents',
      label: 'Documents',
      icon: <FileTextOutlined />,
      roles: ['state', 'admin'],
    },
    {
      path: '/reports',
      label: 'Reports',
      icon: <BarChartOutlined />,
    },
  ];

  const otherNavItems = [
    {
      path: '/contact',
      label: 'Contact',
      icon: <QuestionCircleOutlined />,
    },
    {
      path: '/help',
      label: 'Help',
      icon: <QuestionCircleOutlined />,
    },
    {
      path: '/profile',
      label: 'Settings',
      icon: <SettingOutlined />,
    },
  ];

  const isItemVisible = (item) => {
    if (!item.roles && !item.orgCodes) return true;

    const roleMatch = !item.roles || item.roles.includes(user?.role);
    const orgMatch = !item.orgCodes || item.orgCodes.includes(user?.org_code);

    return roleMatch && orgMatch;
  };

  const isActiveRoute = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item) => {
    if (!isItemVisible(item)) return null;

    return (
      <li key={item.path}>
        <NavLink
          to={item.path}
          exact={item.exact}
          className={`sidebar-nav-item ${isActiveRoute(item.path, item.exact) ? 'active' : ''}`}
          onClick={mobileOpen ? onMobileClose : undefined}
        >
          <span className="sidebar-nav-icon">{item.icon}</span>
          <span className="sidebar-nav-text">{item.label}</span>
          {item.badge && (
            <span className="sidebar-nav-badge">{item.badge}</span>
          )}
        </NavLink>
      </li>
    );
  };

  const renderNavSection = (title, items) => {
    const visibleItems = items.filter(isItemVisible);
    if (visibleItems.length === 0) return null;

    return (
      <div className="sidebar-section">
        {!collapsed && <div className="sidebar-section-title">{title}</div>}
        <ul className="sidebar-nav-list">
          {items.map(renderNavItem)}
        </ul>
      </div>
    );
  };

  const getUserInitials = () => {
    if (!user?.short_name) return 'U';
    const names = user.short_name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return user.short_name.substring(0, 2).toUpperCase();
  };

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${mobileOpen ? 'visible' : ''}`}
        onClick={onMobileClose}
      />

      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Toggle button */}
        <button className="sidebar-toggle" onClick={onToggle} aria-label="Toggle sidebar">
          <LeftOutlined />
        </button>

        {/* Logo */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">
            <DashboardOutlined />
          </div>
          <div className="sidebar-logo-text">
            <span className="logo-title">YD Monitoring</span>
            <span className="logo-subtitle">System Dashboard</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {renderNavSection('Main', mainNavItems)}
          {renderNavSection('E-Library', libraryNavItems)}
          {renderNavSection('Documents', documentNavItems)}
          {renderNavSection('Other', otherNavItems)}
        </nav>

        {/* User section */}
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">
            {getUserInitials()}
          </div>
          <div className="sidebar-user-info">
            <div className="user-name">{user?.short_name || 'User'}</div>
            <div className="user-role">{user?.role || 'Member'}</div>
          </div>
        </div>
      </aside>
    </>
  );
};

const mapStateToProps = ({ AuthReducer }) => ({
  user: AuthReducer.user,
});

export default connect(mapStateToProps)(Sidebar);
